import { downloadCSV, type Exporter, type RaRecord } from 'react-admin';
import { warsawParts } from './dateTime';

/**
 * Eksport list do CSV z polskimi nagłówkami i spłaszczonymi relacjami.
 *
 * Domyślny eksporter react-admina (jsonexport) oddawał surowe nazwy pól po
 * angielsku, a obiekty relacji (`driver`, `company`) jako puste kolumny —
 * planista dostawał plik, którego nie da się przeczytać bez znajomości API
 * (e2e na dev 28.09.2026). Tu kolumny są wypisane wprost: nagłówek po
 * polsku i funkcja, która z wiersza robi tekst.
 *
 * Separator `;` i BOM: tak Excel w polskich ustawieniach otwiera plik bez
 * kreatora importu i bez krzaków w ogonkach.
 */

/**
 * Ile wierszy eksport pobiera jednym żądaniem — ten sam sufit co w API
 * (`apps/api/src/shared/list-range.ts`, `MAX_ROWS_PER_REQUEST`).
 * Domyślne `maxResults` react-admina to 1000, więc bank zleceń ucinałby się
 * po cichu na tysiącu.
 */
export const EXPORT_MAX_ROWS = 5000;

export type CsvColumn = {
  header: string;
  value: (r: RaRecord) => unknown;
};

const SEPARATOR = ';';

/** Wartość komórki jako tekst: daty lokalnie, logiczne jako tak/nie, brak jako pusto. */
export function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'tak' : 'nie';
  if (v instanceof Date) return formatDateTime(v);
  return String(v);
}

/** Chwila jako `RRRR-MM-DD GG:MM` w czasie polskim — jak na ekranie, nie w strefie przeglądarki. */
function formatDateTime(d: Date): string {
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  const w = warsawParts(d);
  return `${w.year}-${p(w.month)}-${p(w.day)} ${p(w.hour)}:${p(w.minute)}`;
}

/** Data z API (ISO) do komórki; nieprawidłowa = pusto, nie „Invalid Date”. */
export function dateCell(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  return formatDateTime(new Date(String(v)));
}

function escapeCell(text: string): string {
  // Cudzysłów, separator albo koniec linii w treści = komórka w cudzysłowie.
  return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Cały plik CSV (z BOM) dla podanych kolumn. */
export function toCsv(rows: readonly RaRecord[], columns: readonly CsvColumn[]): string {
  const lines = [columns.map((c) => escapeCell(c.header)).join(SEPARATOR)];
  for (const r of rows) {
    lines.push(columns.map((c) => escapeCell(cellText(c.value(r)))).join(SEPARATOR));
  }
  return `﻿${lines.join('\r\n')}\r\n`;
}

/** Eksporter dla `<List exporter={…}>` zapisujący plik pod podaną nazwą. */
export function csvExporter(columns: readonly CsvColumn[], filename: string): Exporter {
  return (records) => downloadCSV(toCsv(records as RaRecord[], columns), filename);
}

/** Imię i nazwisko z relacji osoby (`{ name, surname }`). */
export function personName(p: unknown): string {
  if (!p || typeof p !== 'object') return '';
  const o = p as { name?: unknown; surname?: unknown };
  return [o.name, o.surname].filter((x) => typeof x === 'string' && x.trim() !== '').join(' ');
}

/** Pole z relacji (`record.company.name`); brak relacji = pusto. */
export function relationField(rel: unknown, field: string): unknown {
  if (!rel || typeof rel !== 'object') return null;
  return (rel as Record<string, unknown>)[field] ?? null;
}
